import type {
  CreateInboxV2ReplyInput,
  InboxV2Conversation,
  InboxV2ConversationMessage,
  InboxV2SenderProfile,
  InboxV2SystemConversationMessage,
  UpdateInboxV2ConversationInput,
  UpdateInboxV2MessageInput,
} from "../../types";
import { createInboxV2ConfirmedSystemMessage } from "../store/message/state";

export function buildConversationUpdateSystemMessages(input: {
  currentConversation: InboxV2Conversation;
  input: UpdateInboxV2ConversationInput;
  updatedConversation: InboxV2Conversation;
}) {
  const actorName = input.input.systemMessageActorName?.trim() || "User";
  const messages: InboxV2SystemConversationMessage[] = [];

  if (
    input.input.assigneeId &&
    input.input.assigneeId !== input.currentConversation.assigneeId
  ) {
    messages.push(
      createInboxV2ConfirmedSystemMessage({
        actorName,
        assigneeName: input.input.systemMessageAssigneeName,
        type: "assign",
      })
    );
  }

  if (
    input.input.status &&
    input.input.status !== input.currentConversation.status &&
    input.input.status === input.updatedConversation.status
  ) {
    messages.push(
      createInboxV2ConfirmedSystemMessage({
        actorName,
        status: input.updatedConversation.status,
        type: "status",
      })
    );
  }

  return messages;
}

export function buildReplySystemMessages(input: {
  conversation: InboxV2Conversation;
  input: CreateInboxV2ReplyInput;
  message: InboxV2ConversationMessage;
}) {
  if (input.input.isInternal) {
    return [];
  }

  const nextStatus = getReplyNextConversationStatus(
    input.conversation,
    input.input
  );

  if (nextStatus === input.conversation.status) {
    return [];
  }

  return [
    createInboxV2ConfirmedSystemMessage({
      actorName: input.input.systemMessageActorName?.trim() || "User",
      createdAt: input.message.createdAt,
      status: nextStatus,
      type: "status",
    }),
  ];
}

function getReplyNextConversationStatus(
  conversation: InboxV2Conversation,
  input: CreateInboxV2ReplyInput
) {
  if (input.isInternal) {
    return conversation.status;
  }

  if (input.closeConversation) {
    return "closed" as const;
  }

  return conversation.status === "closed"
    ? ("open" as const)
    : conversation.status;
}

export function mapSenderProfileDto(profile: {
  id: string;
  platform?: string;
  platformId: string;
  name: string;
  image?: string | null;
  handle: string;
  followersCount?: number;
  followingCount?: number;
  followingHim?: boolean;
  followingMe?: boolean;
  postsCount?: number;
  updatedAt?: string;
  refreshError?: InboxV2SenderProfile["refreshError"];
}): InboxV2SenderProfile {
  return {
    id: profile.id,
    platform: mapSenderProfilePlatform(profile.platform),
    platformId: profile.platformId,
    name: profile.name,
    image: profile.image,
    handle: profile.handle,
    followersCount: profile.followersCount,
    followingCount: profile.followingCount,
    followingHim: profile.followingHim,
    followingMe: profile.followingMe,
    postsCount: profile.postsCount,
    updatedAt: profile.updatedAt,
    refreshError: profile.refreshError,
  };
}

export function mapSenderProfilePlatform(
  platform?: string
): InboxV2SenderProfile["platform"] {
  switch (platform) {
    case "FB":
      return "facebook";
    case "IG":
      return "instagram";
    case "LI":
      return "linkedin";
    case "TT":
      return "tiktok";
    case "GMB":
      return "google-business";
    default:
      return undefined;
  }
}

export function mapSenderProfilePlatformToApi(platform: string) {
  switch (platform) {
    case "facebook":
      return "FB";
    case "instagram":
      return "IG";
    case "linkedin":
      return "LI";
    case "tiktok":
      return "TT";
    case "google-business":
      return "GMB";
    default:
      return platform;
  }
}

export function mapInboxV2SentimentToLegacyValue(
  sentiment: UpdateInboxV2MessageInput["sentiment"]
) {
  switch (sentiment) {
    case "positive":
      return 1;
    case "negative":
      return 2;
    case "neutral":
      return 3;
    case "mixed":
      return 4;
    default:
      return 5;
  }
}

export function mapInboxV2StatusToConversationStatus(
  status?: UpdateInboxV2ConversationInput["status"]
) {
  if (!status) {
    return;
  }

  return status === "closed" ? "CLOSED" : "OPEN";
}
